import { TrendingUp, TrendingDown, DollarSign, CreditCard, RefreshCcw, Activity, Wallet, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStripeData } from "@/hooks/useStripeData";
import { useTranslation } from "react-i18next";

interface StatCardProps {
  title: string;
  value: string;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  color: 'yellow' | 'cyan' | 'pink' | 'blue' | 'white';
  icon: LucideIcon;
}

function StatCard({ title, value, change, trend, color, icon: Icon }: StatCardProps) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith('en');
  
  const getBgColor = (c: string) => {
    switch (c) {
      case 'yellow': return 'bg-[#FFD93D]';
      case 'cyan': return 'bg-[#6FEDD6]';
      case 'pink': return 'bg-[#FF6B9D]';
      case 'blue': return 'bg-[#4DA6FF]';
      default: return 'bg-white';
    }
  };


  const renderTitle = () => {
    if (!isEnglish) {
      return <span className="text-sm font-bold whitespace-nowrap pt-1">{title}</span>;
    }

    // 特殊處理英文括號：將括號內容放至第二行，縮小且不加粗
    const match = title.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      const [_, main, sub] = match;
      return (
        <div className="flex flex-col gap-0.5 pt-0.5 min-h-[2.5rem]">
          <span className="text-sm font-bold leading-tight">{main}</span>
          <span className="text-[10px] font-medium text-black/60 leading-tight uppercase tracking-wider">{sub}</span>
        </div>
      );
    }

    return <span className="text-sm font-bold whitespace-normal leading-tight pt-1">{title}</span>;
  };

  return (
    <div className={cn(
      "h-full rounded-xl border-2 border-black p-5 transition-transform hover:-translate-y-1 shadow-[4px_4px_0_#000]",
      getBgColor(color)
    )}>
      {/* 頂部：標題 + 圖標 */}
      <div className="flex justify-between items-start mb-4 gap-2">
        {renderTitle()}
        <div className="w-10 h-10 bg-white border-2 border-black rounded-lg flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
      </div>
      
      {/* 金額 */}
      <div className="text-3xl font-black mb-2 tracking-tight">{value}</div>
      
      {/* 變化率 - 所有卡片都要有這個區域 */}
      {(change || trend) && (
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="bg-white/50 px-2 py-0.5 rounded border-2 border-black text-xs">
             {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'} {change || '0%'}
          </span>
          <span className="text-black/60 text-xs">{t('admin.finance.stats.vsLastMonth')}</span>
        </div>
      )}
    </div>
  );
}


export default function RevenueStats() {
  const { t, i18n } = useTranslation();
  const { stats, isLoading } = useStripeData();

  // Parse stats or use defaults if loading/error
  const mrr = stats?.mrr || 0;
  const arr = mrr * 12;
  const monthlyRevenue = stats?.monthly_revenue || 0;
  const monthlyRefunds = stats?.monthly_refunds || 0;

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  if (isLoading) {
      return (
        <div className="mb-8 space-y-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[1,2,3,4].map(i => (
                    <div key={i} className="h-40 rounded-xl border-2 border-black bg-gray-100 animate-pulse shadow-[4px_4px_0_#000]" />
                ))}
            </div>
        </div>
      );
  }

  return (
    <div className="mb-8 space-y-8">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 items-stretch">
        <StatCard 
          title={t('admin.finance.stats.mrr')} 
          value={formatCurrency(mrr)} 
          change="+0%" 
          trend="up" 
          color="yellow" 
          icon={Activity}
        />
        <StatCard 
          title={t('admin.finance.stats.arr')} 
          value={formatCurrency(arr)}
          change="+0%" 
          trend="up" 
          color="cyan" 
          icon={DollarSign}
        />
        <StatCard 
          title={t('admin.finance.stats.monthlyRevenue')} 
          value={formatCurrency(monthlyRevenue)}
          change="+0%" 
          trend="down" 
          color="pink" 
          icon={CreditCard}
        />
        <StatCard 
          title={t('admin.finance.stats.monthlyRefunds')} 
          value={formatCurrency(monthlyRefunds)}
          trend="neutral" 
          color="blue" 
          icon={RefreshCcw}
        />
      </div>

      {/* Secondary Stats Section - Sales Cost */}
      <div>
         <h3 className="mb-4 text-lg font-bold">{t('admin.finance.stats.costsAndExpenses')}</h3>
         <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard 
              title={t('admin.finance.stats.salesCost')} 
              value={formatCurrency(stats?.monthly_sales_cost || 0)}
              trend="neutral" 
              color="white" 
              icon={Wallet}
            />
         </div>
      </div>
    </div>
  );
}
